import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ApplicationProps } from '../../../redux-store/types/api-types';
import {
  isOverseasBank,
  isPaymentStepComplete,
  scrollToBottom,
} from '../../../utils/utilityFunctions';
import { showError } from '../../../redux-store/actions/auth';
import {
  createLumpsumPayment,
  getAvailableBanks,
  getPaymentStatus,
  updatePaymentType,
} from '../store/actions/payment';

interface UsePaymentFlowParams {
  application?: ApplicationProps;
}

export function usePaymentFlow({ application }: UsePaymentFlowParams) {
  const dispatch = useDispatch();

  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [showPaymentSuccessPopup, setShowPaymentSuccessPopup] = useState(false);
  const [showPaymentFailurePopup, setShowPaymentFailurePopup] = useState(false);
  const [showPaymentModePopup, setShowPaymentModePopup] = useState(false);
  const [showOfflinePaymentEsignPopup, setShowOfflinePaymentEsignPopup] = useState(false);
  const [isOnlinePaymentEnabled, setIsOnlinePaymentEnabled] = useState(false);
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);

  // Derive paymentCompleted once application loads (e.g. on refresh)
  useEffect(() => {
    if (!application) return;
    const paymentStatus = application?.payment?.status;
    const paymentType = application?.paymentType;
    if (
      (paymentStatus &&
        ['pending', 'success', 'failure', 'expired'].includes(paymentStatus || '') &&
        paymentType === 'online') ||
      paymentType === 'offline'
    ) {
      setPaymentCompleted(true);
    }
  }, [application]);

  // Handle redirect back from payment gateway (order_id / status query params)
  useEffect(() => {
    (async function () {
      const urlParams = new URLSearchParams(window.location.search);
      const actualStatus = urlParams.get('status')?.toLowerCase();
      const orderId = urlParams.get('order_id');
      const clearUrlParams = () =>
        window.history.replaceState({}, document.title, window.location.pathname);

      if (['pending', 'success'].includes(actualStatus || '')) {
        scrollToBottom();
      }

      if (orderId) {
        try {
          const paymentStatusResponse = (await dispatch(getPaymentStatus(orderId))) as unknown as {
            status?: string;
          };
          const status = (paymentStatusResponse?.status || '').toLowerCase();
          if (['pending', 'success'].includes(status)) {
            clearUrlParams();
            setShowPaymentSuccessPopup(true);
            setPaymentCompleted(true);
          } else if (['failure', 'expired'].includes(status)) {
            clearUrlParams();
            setShowPaymentFailurePopup(true);
          }
        } catch (e) {
          typeof e === 'string' && dispatch(showError(e as string));
          clearUrlParams();
          setShowPaymentFailurePopup(true);
        }
        return;
      }

      if (actualStatus) {
        if (['pending', 'success'].includes(actualStatus)) {
          clearUrlParams();
          setShowPaymentSuccessPopup(true);
          setPaymentCompleted(true);
        } else if (['failure', 'expired'].includes(actualStatus)) {
          clearUrlParams();
          setShowPaymentFailurePopup(true);
        }
      }
    })();
  }, []);

  const handleMakePayment = async () => {
    try {
      setShowPaymentFailurePopup(false);
      if (!application?.id) {
        dispatch(showError('Application details are missing. Please refresh and try again.'));
        return;
      }
      if (application?.paymentUrl) {
        window.open(application.paymentUrl, '_blank');
        return;
      }
      const selectedBank =
        application?.banks?.find((bank) => bank?.defaultBankAccount) || application?.banks?.[0];
      const isOverseas = isOverseasBank(selectedBank?.investorBankType);
      const bankCode = isOverseas ? selectedBank?.swiftCode || '' : selectedBank?.ifscCode || '';

      if (!bankCode) {
        dispatch(
          showError(
            isOverseas
              ? 'Bank SWIFT code is missing. Please update bank details and try again.'
              : 'Bank IFSC code is missing. Please update bank details and try again'
          )
        );
        return;
      }

      setIsPaymentSubmitting(true);
      const availableBanksResponse = (await dispatch(
        getAvailableBanks({
          trtype: 'Lumpsum',
          paymode: 'Net Banking',
          ifsc: bankCode,
        })
      )) as unknown as { isNetbankingAvailable?: boolean };
      setIsOnlinePaymentEnabled(Boolean(availableBanksResponse?.isNetbankingAvailable));
      setShowPaymentModePopup(true);
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
    } finally {
      setIsPaymentSubmitting(false);
    }
  };

  const handlePaymentModeConfirm = async (paymentType: string, paymentMode: string) => {
    try {
      if (!application?.id) {
        dispatch(showError('Application details are missing. Please refresh and try again.'));
        return;
      }
      await dispatch(updatePaymentType({ applicationId: application.id, paymentType }));
      if (paymentMode !== 'offline') {
        const callbackSuccessUrl = `${window.location.origin}${window.location.pathname}?paymentStatus=success`;
        const callbackFailureUrl = `${window.location.origin}${window.location.pathname}?paymentStatus=failed`;
        const paymentCreateResponse = (await dispatch(
          createLumpsumPayment({
            applicationId: application.id,
            payment_mode: paymentMode,
            callback_success_url: callbackSuccessUrl,
            callback_failure_url: callbackFailureUrl,
          })
        )) as unknown as { url?: string; order_id?: string };

        const paymentUrl = paymentCreateResponse?.url;
        if (paymentUrl) {
          window.open(paymentUrl, '_blank');
          return;
        }
        dispatch(showError('Unable to initiate payment. Please try again.'));
      } else {
        setPaymentCompleted(true);
        setShowOfflinePaymentEsignPopup(true);
        scrollToBottom();
      }
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
    }
  };

  const handleContinueOffline = async () => {
    try {
      if (!application?.id) {
        dispatch(showError('Application details are missing. Please refresh and try again.'));
        return;
      }
      await dispatch(updatePaymentType({ applicationId: application.id, paymentType: 'offline' }));
      setShowPaymentFailurePopup(false);
      setShowOfflinePaymentEsignPopup(true);
      setPaymentCompleted(true);
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
    }
  };

  // Generic version of "overseas -> mark offline -> proceed to esign".
  // Pass whatever the consuming component wants to run afterwards (usually handleEsign).
  const markPaymentOfflineAndProceed = async (onProceed?: () => Promise<void> | void) => {
    try {
      if (!application?.id) {
        dispatch(showError('Application details are missing. Please refresh and try again.'));
        return;
      }
      await dispatch(updatePaymentType({ applicationId: application.id, paymentType: 'offline' }));
      setPaymentCompleted(true);
      if (onProceed) await onProceed();
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
    }
  };

  const paymentStepComplete = isPaymentStepComplete(paymentCompleted, application);
  const isOverseas = isOverseasBank(application?.banks?.[0]?.investorBankType || '');

  return {
    paymentCompleted,
    setPaymentCompleted,
    paymentStepComplete,
    isOverseas,
    isPaymentSubmitting,
    isOnlinePaymentEnabled,
    showPaymentSuccessPopup,
    setShowPaymentSuccessPopup,
    showPaymentFailurePopup,
    setShowPaymentFailurePopup,
    showPaymentModePopup,
    setShowPaymentModePopup,
    showOfflinePaymentEsignPopup,
    setShowOfflinePaymentEsignPopup,
    handleMakePayment,
    handlePaymentModeConfirm,
    handleContinueOffline,
    markPaymentOfflineAndProceed,
  };
}
